import { useState } from "react";
import { Box, DialogContent, Link } from "@mui/material";
import { useTranslation } from "react-i18next";
import DialogComponent from "../../Dialog/Dialog";
import PaymentTermsDialog from "./PaymentTermsDialog";
import useProfessionalAccountStatus from "../../../hooks/useProfessionalAccountStatus";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

const reminderSx = {
  ...typography.body.l,
  color: semanticColors.text.primary,
  cursor: "pointer"
};

/**
 * Holds the payments area shut while revised terms are outstanding.
 *
 * Only a revision blocks. A professional who has never agreed is not being
 * held back from anything — with no payment account there is no history to
 * read and nothing to charge — so they browse freely and meet the terms as the
 * first step of registering an account, which is where agreeing actually
 * matters. See `paymentTermsRevised` for why the two are told apart.
 *
 * Closing the terms leaves the reminder standing rather than letting the area
 * through: it states what is needed and reopens the terms when clicked, so the
 * gate cannot be dismissed, only satisfied.
 */
export function withPaymentTerms<P extends object>(
  Component: React.ComponentType<P>
) {
  return function Gated(props: P) {
    return (
      <PaymentTermsGate>
        <Component {...props} />
      </PaymentTermsGate>
    );
  };
}

export default function PaymentTermsGate({
  children
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { paymentTermsRevised, loading } = useProfessionalAccountStatus();
  const [showingTerms, setShowingTerms] = useState(true);

  if (loading || !paymentTermsRevised) return <>{children}</>;

  return (
    <>
      <PaymentTermsDialog
        open={showingTerms}
        onClose={() => setShowingTerms(false)}
      />

      <DialogComponent
        open={!showingTerms}
        handleModal={() => setShowingTerms(true)}
        headerTxt={t("paymentTerms.title")}
      >
        <DialogContent>
          <Box sx={{ pb: 2 }}>
            <Link
              component="button"
              type="button"
              underline="always"
              onClick={() => setShowingTerms(true)}
              sx={reminderSx}
            >
              {t("paymentTerms.reviewReminder")}
            </Link>
          </Box>
        </DialogContent>
      </DialogComponent>
    </>
  );
}
